import {
  Bike,
  Crown,
  Flame,
  Ghost,
  Handshake,
  Rocket,
  Shield,
  Skull,
  Star,
  Swords,
  Target,
  Trophy,
  Zap,
} from "lucide-react";
import config from "../../config";

// Emblem keys must match the server's utils/teamIdentity.js TEAM_EMBLEMS.
export const TEAM_EMBLEMS = {
  flame: Flame,
  zap: Zap,
  shield: Shield,
  rocket: Rocket,
  crown: Crown,
  trophy: Trophy,
  swords: Swords,
  target: Target,
  star: Star,
  skull: Skull,
  ghost: Ghost,
  bike: Bike,
};
const EMBLEM_KEYS = Object.keys(TEAM_EMBLEMS);

export const TEAM_COLORS = [
  "#FF9200",
  "#120239",
  "#E11D48",
  "#7C3AED",
  "#0284C7",
  "#059669",
  "#CA8A04",
  "#DB2777",
  "#0F766E",
  "#475569",
];

export const TEAM_NAME_MIN = 3;
export const TEAM_NAME_MAX = 24;

export const getTeamColor = (team) =>
  team?.color || TEAM_COLORS[Number(team?.id || 0) % TEAM_COLORS.length];

export const getTeamEmblemKey = (team) =>
  team?.emblem && TEAM_EMBLEMS[team.emblem]
    ? team.emblem
    : EMBLEM_KEYS[Number(team?.id || 0) % EMBLEM_KEYS.length];

// Pick white or navy foreground for legible icons/text on a team color.
export const getContrastColor = (hex) => {
  const value = (hex || "").replace("#", "");
  if (value.length !== 6) return "#FFFFFF";
  const [r, g, b] = [0, 2, 4].map((i) => {
    const channel = parseInt(value.slice(i, i + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.35 ? "#120239" : "#FFFFFF";
};

export const getAvatarUrl = (user) =>
  user?.profile_pic ? `${config.apiUrl}/assets/uploads/${user.profile_pic}` : null;

export const getDisplayName = (user) =>
  user?.full_name || user?.user_name || "Player";

export const getInitials = (user) =>
  getDisplayName(user)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

const NAME_ADJECTIVES = [
  "Thunder", "Turbo", "Neon", "Blazing", "Rapid", "Iron", "Shadow", "Cosmic",
  "Savage", "Electric", "Golden", "Wild", "Atomic", "Midnight", "Hyper", "Nitro",
];
const NAME_NOUNS = [
  "Pedals", "Riders", "Comets", "Wolves", "Titans", "Rockets", "Falcons", "Vipers",
  "Legends", "Spartans", "Storm", "Squad", "Crew", "Raptors", "Sprinters", "Dragons",
];

const pick = (list) => list[Math.floor(Math.random() * list.length)];

/** Random fun team name that passes the server's rules (3-24 chars). */
export const suggestTeamName = (current = "") => {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const name = `${pick(NAME_ADJECTIVES)} ${pick(NAME_NOUNS)}`;
    if (name !== current && name.length <= TEAM_NAME_MAX) return name;
  }
  return "Nitro Squad";
};

/** Client-side mirror of the server's name rules, for inline validation. */
export const validateTeamName = (name, t) => {
  const value = (name || "").trim().replace(/\s+/g, " ");
  if (value.length < TEAM_NAME_MIN || value.length > TEAM_NAME_MAX) {
    return t("teamNameLength", {
      defaultValue: "Use {{min}}-{{max}} characters.",
      min: TEAM_NAME_MIN,
      max: TEAM_NAME_MAX,
    });
  }
  if (!/^[\p{L}\p{N} _.\-!'&]+$/u.test(value)) {
    return t("teamNameChars", {
      defaultValue: "Only letters, numbers, spaces and _ . - ! ' & are allowed.",
    });
  }
  if (/^team\s*\d+$/i.test(value)) {
    return t("teamNameDefault", {
      defaultValue: "Pick something more original than \"Team<number>\".",
    });
  }
  return null;
};

export const formatDuration = (value) => {
  const total = Number(value);
  if (value === null || value === undefined || !Number.isFinite(total)) return "-";
  const totalSeconds = Math.floor(total / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const ms = total % 1000;
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
};

export const formatNumber = (value, locale) =>
  value === null || value === undefined ? "-" : new Intl.NumberFormat(locale).format(value);

export const formatRelativeTime = (value, locale) => {
  if (!value) return null;
  const diffSeconds = (new Date(value).getTime() - Date.now()) / 1000;
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  for (const [unit, seconds] of units) {
    if (Math.abs(diffSeconds) >= seconds) {
      return formatter.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return formatter.format(0, "minute");
};

export const formatDateTime = (value, locale) => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

/**
 * Achievement badges derived from team stats (and rivals, when loaded).
 * Each badge carries progress so locked ones show how close the team is.
 */
export const getTeamBadges = (team, rivals = []) => {
  const stats = team?.stats || {};
  const bestRivalWins = rivals.reduce((max, rival) => Math.max(max, rival.wins || 0), 0);
  const badges = [
    { key: "firstWin", icon: Trophy, current: stats.wins || 0, target: 1 },
    {
      key: team?.isSolo ? "dedicated" : "squadGoals",
      icon: team?.isSolo ? Target : Handshake,
      current: stats.gamesPlayed || 0,
      target: 10,
    },
    { key: "hotStreak", icon: Flame, current: stats.bestWinStreak || 0, target: 3 },
    { key: "unstoppable", icon: Zap, current: stats.bestWinStreak || 0, target: 5 },
    { key: "rivalSlayer", icon: Swords, current: bestRivalWins, target: 3 },
  ];
  if (!team?.isSolo) {
    badges.push({ key: "coopMasters", icon: Shield, current: stats.sessionTypes?.Coop || 0, target: 5 });
  }
  return badges.map((badge) => ({
    ...badge,
    earned: badge.current >= badge.target,
    progress: Math.min(1, badge.current / badge.target),
  }));
};

export const BADGE_TEXT = {
  firstWin: { title: "First Blood", hint: "Win a match" },
  squadGoals: { title: "Squad Goals", hint: "Play 10 games together" },
  dedicated: { title: "Dedicated", hint: "Play 10 games" },
  hotStreak: { title: "Hot Streak", hint: "Win 3 matches in a row" },
  unstoppable: { title: "Unstoppable", hint: "Win 5 matches in a row" },
  rivalSlayer: { title: "Rival Slayer", hint: "Beat the same team 3 times" },
  coopMasters: { title: "Co-op Masters", hint: "Clear 5 co-op games" },
};
